import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Copy, MessageCircle, MessageSquare, Mail, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { waLink, smsLink, gmailLink, outlookLink } from "@/lib/crm";
import { AiDraft } from "@/components/ai/AiDraft";
import { ConsentBadge, consentStatus } from "@/components/ConsentBadge";

const ChannelBtn = ({ testid, onClick, icon: I, label, cls, disabled }) => (
  <button data-testid={testid} disabled={disabled} onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-[background-color,opacity] disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}>
    <I className="h-4 w-4" /> {label}
  </button>
);

const PurposeBox = ({ purpose, setPurpose, status, exceptions, override, setOverride, reason, setReason }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between"><Label className="text-xs text-slate-500">Tipo di comunicazione</Label><ConsentBadge client={status.client} testid="message-consent-badge" /></div>
    <Select value={purpose} onValueChange={(v) => { setPurpose(v); setOverride(false); }}>
      <SelectTrigger data-testid="message-purpose-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="operativa">Operativa (pratica, documenti, appuntamenti, rinnovi)</SelectItem>
        <SelectItem value="promozionale">Promozionale (offerte, nuovi prodotti, auguri)</SelectItem>
      </SelectContent>
    </Select>
    {purpose === "promozionale" && status.value !== "attivo" && (
      <div data-testid="marketing-consent-warning" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900 space-y-2">
        <p className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 shrink-0" /><span>Consenso marketing <b>{status.value}</b>: l'invio promozionale è <b>bloccato</b> e il tentativo verrà registrato.{!exceptions && " Le eccezioni sono disattivate in Impostazioni."}</span></p>
        {exceptions && (
          <div className="space-y-2 border-t border-rose-200 pt-2" data-testid="exception-box">
            <label className="flex items-start gap-2 cursor-pointer"><Checkbox data-testid="marketing-override-checkbox" checked={override} onCheckedChange={(v) => setOverride(!!v)} className="mt-0.5" /><span>Dichiaro di aver verificato l'esistenza di una base giuridica applicabile diversa dal consenso e mi assumo la responsabilità di questa scelta.</span></label>
            {override && <Textarea data-testid="exception-reason-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} placeholder="Motivazione specifica (almeno 20 caratteri), es. Cliente ex art. 130 c.4, servizio analogo già acquistato" className="bg-white text-xs" />}
            {override && reason.trim().length < 20 && <p className="text-[11px]" data-testid="exception-reason-hint">Motivazione obbligatoria: almeno 20 caratteri ({reason.trim().length}/20).</p>}
            <p data-testid="exception-legal-warning" className="text-[11px] text-rose-800">Attenzione: questa dichiarazione non rende automaticamente lecito l'invio. Restano applicabili tutti i requisiti di legge (GDPR, Codice privacy, norme su comunicazioni commerciali). Agente, cliente, data e ora, canale, motivazione e operazione verranno registrati.</p>
          </div>
        )}
      </div>
    )}
  </div>
);

export const MessageDialog = ({ open, onOpenChange, client }) => {
  const { data: templates = [] } = useApi("/templates", { enabled: open });
  const [tid, setTid] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [purpose, setPurpose] = useState("operativa");
  const [override, setOverride] = useState(false);
  const [copyMedium, setCopyMedium] = useState("");
  const [reason, setReason] = useState("");
  const refresh = useRefresh();

  useEffect(() => { if (open) { setTid(""); setSubject(""); setBody(""); setPurpose("operativa"); setOverride(false); setCopyMedium(""); setReason(""); } }, [open]);
  const { data: st } = useApi("/settings", { enabled: open });
  const promo = purpose === "promozionale";
  const status = consentStatus(client);
  const exceptions = st?.promo_exceptions_enabled === true;

  const pick = async (id) => {
    setTid(id);
    const { data } = await api.get(`/templates/${id}/render/${client.client_id}`);
    setSubject(data.subject);
    setBody(data.body);
  };

  const log = (type, content) => api.post("/interactions", { client_id: client.client_id, type, content }).then(refresh).catch(() => {});
  const gate = async (channel) => {
    if (!promo) return "";
    try {
      const { data } = await api.post(`/clients/${client.client_id}/promo-check`, { channel, medium: channel === "copia" ? copyMedium : null, subject: subject || body.slice(0, 120), confirm_exception: override, exception_reason: override ? reason : "" });
      refresh();
      return data.outcome === "inviato_eccezione" ? "[Promozionale, con eccezione] " : "[Promozionale] ";
    } catch (e) { refresh(); toast.error(errMsg(e)); return null; }
  };
  const open_ = async (url, type, label, channel) => {
    const w = promo ? window.open("about:blank", "_blank") : null;
    const tag = await gate(channel);
    if (tag === null) { if (w) w.close(); return; }
    if (w) { w.opener = null; w.location.href = url; } else window.open(url, "_blank", "noopener");
    log(type, `${tag}${label}: ${body.slice(0, 140)}`);
  };
  const copy = async () => {
    if (!promo) { await navigator.clipboard.writeText(body); toast.success("Testo copiato"); return; }
    if (!copyMedium) { toast.error("Scegli prima su quale canale userai il testo"); return; }
    let tag = null;
    const blob = gate("copia").then((t) => { tag = t; if (t === null) throw new Error("bloccato"); return new Blob([body], { type: "text/plain" }); });
    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      toast.success("Testo copiato");
      log("nota", `${tag}Testo copiato: ${body.slice(0, 140)}`);
    } catch { if (tag !== null) toast.error("Copia non riuscita"); }
  };

  const sendResend = async () => {
    setSending(true);
    try {
      await api.post(`/clients/${client.client_id}/send-email`, { template_id: tid, purpose, confirm_no_consent: override, exception_reason: override ? reason : "" });
      toast.success(`Email inviata a ${client.email}`);
      refresh();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); refresh(); } finally { setSending(false); }
  };

  const noPhone = !client?.phone, noMail = !client?.email;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="message-dialog">
        <DialogHeader><DialogTitle className="font-display">Messaggio a {client?.first_name} {client?.last_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <AiDraft clientId={client?.client_id} onDraft={(s, b) => { if (s !== null) setSubject(s); setBody(b); setTid(""); }} />
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Modello</Label>
            <Select value={tid} onValueChange={pick}>
              <SelectTrigger data-testid="message-template-select"><SelectValue placeholder="Scegli un modello (o scrivi liberamente)" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <PurposeBox purpose={purpose} setPurpose={setPurpose} status={{ value: status, client }} exceptions={exceptions} override={override} setOverride={setOverride} reason={reason} setReason={setReason} />
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Oggetto (email)</Label><Input data-testid="message-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Testo</Label><Textarea data-testid="message-body" rows={7} value={body} onChange={(e) => setBody(e.target.value)} /></div>

          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Messaggistica</p>
            <div className="grid grid-cols-3 gap-2">
              <ChannelBtn testid="send-whatsapp-btn" disabled={noPhone || !body} onClick={() => open_(waLink(client.phone, body), "whatsapp", "WhatsApp", "whatsapp")} icon={MessageCircle} label="WhatsApp" cls="bg-[#25D366] text-white hover:bg-[#1ebe5a]" />
              <ChannelBtn testid="send-sms-btn" disabled={noPhone || !body} onClick={() => open_(smsLink(client.phone, body), "sms", "SMS", "sms")} icon={MessageSquare} label="SMS" cls="bg-slate-800 text-white hover:bg-slate-700" />
              {promo && (
                <div className="col-span-3">
                  <Select value={copyMedium} onValueChange={setCopyMedium}>
                    <SelectTrigger data-testid="copy-medium-select" className="h-9 text-xs"><SelectValue placeholder="Per copiare un testo promozionale, scegli su quale canale lo userai" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="telefono">Telefono</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <ChannelBtn testid="copy-message-btn" disabled={!body} onClick={copy} icon={Copy} label="Copia" cls="border border-slate-200 hover:bg-slate-50" />
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Email {noMail && <span className="normal-case text-rose-500">· il cliente non ha un'email</span>}</p>
            <div className="grid grid-cols-3 gap-2">
              <ChannelBtn testid="open-gmail-btn" disabled={noMail || !body} onClick={() => open_(gmailLink(client.email, subject, body), "email", "Email (Gmail)", "gmail")} icon={Mail} label="Apri in Gmail" cls="border border-slate-200 hover:bg-slate-50" />
              <ChannelBtn testid="open-outlook-btn" disabled={noMail || !body} onClick={() => open_(outlookLink(client.email, subject, body), "email", "Email (Outlook)", "outlook")} icon={Mail} label="Apri in Outlook" cls="border border-slate-200 hover:bg-slate-50" />
              <ChannelBtn testid="send-resend-btn" disabled={noMail || !tid || sending} onClick={sendResend} icon={Send} label={sending ? "Invio…" : "Invia dal CRM"} cls="bg-sky-600 text-white hover:bg-sky-700" />
            </div>
            <p className="text-xs text-slate-400 mt-2">"Invia dal CRM" spedisce il modello selezionato con la tua firma; le risposte arrivano alla tua casella. Le bozze AI e i testi personalizzati si inviano con WhatsApp, SMS, Gmail o Outlook.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
